import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useDndMonitor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  type SortingStrategy,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type React from "react";
import { Fragment, type ReactNode } from "react";

/** Props to spread on a drag-handle element when `Sortable` is used with `handle`. */
export interface SortableHandleProps {
  ref: (element: HTMLElement | null) => void;
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
}

interface SortableItemProps {
  id: string;
  children: ReactNode | ((handle: SortableHandleProps) => ReactNode);
  disabled?: boolean;
  handle?: boolean;
}

function SortableItem({ id, children, disabled = false, handle = false }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  // Handle mode: only the wired handle starts a drag, so inputs inside the card stay usable.
  if (handle && typeof children === "function") {
    return (
      <div ref={setNodeRef} style={style}>
        {children({ ref: setActivatorNodeRef, attributes, listeners })}
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children as ReactNode}
    </div>
  );
}

export interface ReorderInfo {
  oldIndex: number;
  newIndex: number;
}

interface SortableProps<T extends { id: string }> {
  items: T[];
  onReorder: (newItems: T[], info: ReorderInfo) => void;
  children: (item: T, index: number, handle?: SortableHandleProps) => ReactNode;
  disabled?: boolean;
  strategy?: SortingStrategy;
  // When true, `children` gets handle props as its 3rd arg and only that element starts a drag.
  handle?: boolean;
  // Extra nodes placed after an item as SIBLINGS of it, so a grid lays them out as their own
  // cells rather than inside the dragged one. They are not sortable.
  after?: (item: T, index: number) => ReactNode;
  /**
   * Reorder inside the SURROUNDING DndContext instead of a private one. Required when the items
   * contain drop zones belonging to that context — a nested context would own them instead, and
   * a drag started outside could never land on them.
   */
  ambient?: boolean;
}

function createReorderHandler<T extends { id: string }>(
  items: T[],
  onReorder: SortableProps<T>["onReorder"]
) {
  return ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    // Ambient mode shares its context with other drags and drop zones, so an end event here is
    // not necessarily a reorder of these items.
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(items, oldIndex, newIndex), { oldIndex, newIndex });
  };
}

function SortableItems<T extends { id: string }>({
  items,
  children,
  disabled = false,
  strategy = rectSortingStrategy,
  handle = false,
  after,
}: Omit<SortableProps<T>, "onReorder" | "ambient">) {
  return (
    <SortableContext items={items.map((item) => item.id)} strategy={strategy}>
      {items.map((item, index) => (
        <Fragment key={item.id}>
          <SortableItem id={item.id} disabled={disabled ?? false} handle={handle}>
            {handle
              ? (hp: SortableHandleProps) => children(item, index, hp)
              : children(item, index)}
          </SortableItem>
          {after?.(item, index)}
        </Fragment>
      ))}
    </SortableContext>
  );
}

function AmbientSortable<T extends { id: string }>({ onReorder, ...rest }: SortableProps<T>) {
  useDndMonitor({ onDragEnd: createReorderHandler(rest.items, onReorder) });

  return <SortableItems {...rest} />;
}

function StandaloneSortable<T extends { id: string }>({ onReorder, ...rest }: SortableProps<T>) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={createReorderHandler(rest.items, onReorder)}
    >
      <SortableItems {...rest} />
    </DndContext>
  );
}

export function Sortable<T extends { id: string }>({
  ambient = false,
  ...props
}: SortableProps<T>) {
  return ambient ? <AmbientSortable {...props} /> : <StandaloneSortable {...props} />;
}
